/**
 * parser.ts — Safe mathematical expression parser
 *
 * Uses mathjs to parse and compile user-supplied f(x) expressions.
 * Never uses eval() or Function(). Validates that only allowed
 * symbols are present in the expression.
 */

import { parse, type MathNode } from 'mathjs';

/** Result of parsing a function string */
export interface ParseResult {
  ok: true;
  /** Evaluates f(x) for a given x value */
  evaluate: (x: number) => number;
  /** The original input string, cleaned */
  expression: string;
}

export interface ParseError {
  ok: false;
  /** Human-readable error message for the UI */
  message: string;
}

export type ParseOutcome = ParseResult | ParseError;

/**
 * Allowed function names the player can use.
 * Anything not in this list will be rejected.
 */
const ALLOWED_FUNCTIONS = new Set([
  'sin', 'cos', 'tan',
  'asin', 'acos', 'atan',
  'sinh', 'cosh', 'tanh',
  'sqrt', 'cbrt',
  'abs',
  'log', 'log2', 'log10', 'ln',
  'exp',
  'ceil', 'floor', 'round',
  'sign',
  'min', 'max',
]);

/** Allowed symbolic constants */
const ALLOWED_CONSTANTS = new Set([
  'pi', 'e', 'PI', 'E',
]);

/** The only allowed variable */
const ALLOWED_VARIABLE = 'x';

/**
 * Recursively validates that a mathjs AST node only uses
 * allowed symbols, functions, and operators.
 */
function validateNode(node: MathNode): string | null {
  const type = node.type;

  switch (type) {
    case 'ConstantNode':
      // Numeric literals are always fine
      return null;

    case 'SymbolNode': {
      const name = (node as any).name as string;
      if (name === ALLOWED_VARIABLE) return null;
      if (ALLOWED_CONSTANTS.has(name)) return null;
      return `Variable "${name}" is not allowed. Only "x" can be used.`;
    }

    case 'FunctionNode': {
      const fnName = (node as any).fn?.name ?? (node as any).name;
      if (!ALLOWED_FUNCTIONS.has(fnName)) {
        return `Function "${fnName}" is not allowed. Allowed functions: ${[...ALLOWED_FUNCTIONS].join(', ')}`;
      }
      // Validate arguments recursively
      const args = (node as any).args as MathNode[];
      for (const arg of args) {
        const err = validateNode(arg);
        if (err) return err;
      }
      return null;
    }

    case 'OperatorNode': {
      const op = (node as any).op as string;
      const allowedOps = ['+', '-', '*', '/', '^', '%'];
      if (!allowedOps.includes(op)) {
        return `Operator "${op}" is not allowed.`;
      }
      const args = (node as any).args as MathNode[];
      for (const arg of args) {
        const err = validateNode(arg);
        if (err) return err;
      }
      return null;
    }

    case 'ParenthesisNode': {
      return validateNode((node as any).content as MathNode);
    }

    case 'UnaryMinusNode':
    case 'UnaryPlusNode': {
      // These might appear as OperatorNode with implicit: true
      const args = (node as any).args as MathNode[];
      if (args) {
        for (const arg of args) {
          const err = validateNode(arg);
          if (err) return err;
        }
      }
      return null;
    }

    default:
      return `Invalid expression: type "${type}" not supported.`;
  }
}

/**
 * Parses and validates a mathematical function string.
 *
 * @param input - The raw user input (e.g. "2*x^2 + sin(x)")
 * @returns ParseResult with an evaluator, or ParseError with a message
 */
export function parseFunction(input: string): ParseOutcome {
  // Trim whitespace
  const cleaned = input.trim();

  if (!cleaned) {
    return { ok: false, message: 'Please enter a function, e.g.: x^2 + 1' };
  }

  // Try to parse with mathjs
  let node: MathNode;
  try {
    node = parse(cleaned);
  } catch (err: any) {
    const msg = err?.message ?? 'Syntax error';
    return { ok: false, message: `Syntax error: ${msg}` };
  }

  // Validate the AST — only allowed symbols/functions
  const validationError = validateNode(node);
  if (validationError) {
    return { ok: false, message: validationError };
  }

  // Compile for efficient repeated evaluation
  const compiled = node.compile();

  // Create the evaluator function
  const evaluate = (x: number): number => {
    try {
      const result = compiled.evaluate({ x });
      if (typeof result !== 'number' || !isFinite(result)) {
        return NaN;
      }
      return result;
    } catch {
      return NaN;
    }
  };

  // Sanity check: try evaluating at x=0
  try {
    evaluate(0);
  } catch {
    return { ok: false, message: 'Function cannot be evaluated. Check syntax.' };
  }

  return {
    ok: true,
    evaluate,
    expression: cleaned,
  };
}
